import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { PublicHeader } from '../components/PublicHeader';
import { SiteFooter } from '../components/SiteFooter';
import { useApi } from '../lib/useApi';
import { Alert, Button, Field, Select, TextInput } from '../components/ui';
import { useMutation, compact, numOrUndef } from '../lib/useMutation';
import { FALLBACK_MODEL, bandSum, formatKES, quoteUnits, type PricingResponse } from '../lib/pricing';
import '../styles/landing.css';
import '../styles/public.css';

/**
 * Request a tailored quotation.
 *
 * WHAT IT ASKS, AND WHY IT ASKS MORE THAN IT DID. A quotation turns on six
 * things — units, portfolio scale, users, the modules they need, the
 * integrations they need, and how much work the implementation is. The form
 * used to collect the first two, so every quotation started with a discovery
 * call asking the rest. The questions were being asked either way; the only
 * choice was whether the prospect answered them once, in their own time, or on
 * a phone call somebody had to arrange first.
 *
 * KEEPING IT FAST IS A MATTER OF SHAPE, NOT LENGTH. Four named sections, each a
 * single idea; the multi-answer questions as chips rather than the service
 * cards, because a chip is one word and fourteen of them scan as a row. Only
 * four fields are genuinely required — the ones that let a person reply — and
 * the API refuses none of the rest, so an abandoned question costs a field
 * rather than the lead.
 *
 * THE PRICE STAYS INTERNAL. The brief is explicit that the pricing calculation
 * must not appear on the public form. The standard progressive figure shown
 * beside the unit count is not that calculation: it is the published rate card,
 * the same one on the pricing page, and quoting against a known baseline is a
 * stronger conversation than quoting into silence.
 */

interface Errors { [k: string]: string }

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Mirrors CLIENT_TYPES in src/domain/pricing.ts — the server validates these. */
const CLIENT_TYPES: [string, string][] = [
  ['individual_landlord', 'Individual landlord'],
  ['property_manager', 'Property manager'],
  ['property_company', 'Property management company'],
  ['real_estate', 'Real estate company'],
  ['developer', 'Developer'],
  ['owner_investor', 'Property owner / investor'],
  ['other', 'Other'],
];

const PROPERTY_TYPES: [string, string][] = [
  ['apartments', 'Apartments'],
  ['residential', 'Residential houses'],
  ['commercial', 'Commercial'],
  ['mixed_use', 'Mixed-use'],
  ['student', 'Student housing'],
  ['short_stay', 'Short-stay / serviced'],
  ['other', 'Other'],
];

const CURRENT_SOFTWARE: [string, string][] = [
  ['none', 'Nothing yet'],
  ['spreadsheets', 'Excel / spreadsheets'],
  ['other_software', 'Other software'],
];

const COLLECTION_METHODS: [string, string][] = [
  ['mpesa', 'M-Pesa'],
  ['bank', 'Bank transfer'],
  ['cash', 'Cash'],
  ['gateway', 'Payment gateway'],
  ['other', 'Other'],
];

const MONTHLY_COLLECTION: [string, string][] = [
  ['under_500k', 'Below KES 500,000'],
  ['500k_2m', 'KES 500,000 – 2M'],
  ['2m_5m', 'KES 2M – 5M'],
  ['5m_10m', 'KES 5M – 10M'],
  ['over_10m', 'Above KES 10M'],
  ['undisclosed', 'Prefer not to say'],
];

const MODULES: [string, string][] = [
  ['rent', 'Rent & collections'],
  ['invoicing', 'Invoicing & receipts'],
  ['tenants', 'Tenant management'],
  ['leases', 'Lease management'],
  ['maintenance', 'Maintenance'],
  ['finance', 'Expenses & finance'],
  ['portfolio', 'Property & unit management'],
  ['inspections', 'Inspections'],
  ['listings', 'Property listings'],
  ['reporting', 'Reporting & analytics'],
  ['ai', 'AI property intelligence'],
  ['tenant_portal', 'Tenant portal'],
  ['owner_portal', 'Owner portal'],
  ['other', 'Other'],
];

const INTEGRATIONS: [string, string][] = [
  ['mpesa', 'M-Pesa'],
  ['etims', 'eTIMS'],
  ['bank', 'Bank integration'],
  ['google_workspace', 'Gmail / Google Workspace'],
  ['microsoft', 'Outlook / Microsoft 365'],
  ['google_drive', 'Google Drive'],
  ['whatsapp', 'WhatsApp'],
  ['other', 'Other'],
];

const USER_COUNTS: [string, string][] = [
  ['1', '1 user'],
  ['2_5', '2–5 users'],
  ['6_10', '6–10 users'],
  ['11_25', '11–25 users'],
  ['26_plus', '26+ users'],
];

const TIMELINES: [string, string][] = [
  ['asap', 'As soon as possible'],
  ['one_month', 'Within a month'],
  ['one_three', '1–3 months'],
  ['three_six', '3–6 months'],
  ['exploring', 'Just exploring'],
];

/**
 * The done-for-you work, kept as cards rather than chips.
 *
 * These are the only options whose NOTE is the point — "we move your existing
 * records across" is what a prospect is actually buying, and it does not fit on
 * a chip. Everything else on this form is a category, not a promise.
 */
const SERVICES: { code: string; label: string; note: string }[] = [
  { code: 'data_migration', label: 'Full data upload / migration', note: 'We move your existing records across.' },
  { code: 'excel_import', label: 'Excel data import & preparation', note: 'We clean and shape your spreadsheets first.' },
  { code: 'system_setup', label: 'Done-for-you system setup', note: 'We configure the platform end to end.' },
  { code: 'data_loading', label: 'Tenant, property & unit loading', note: 'We enter your portfolio for you.' },
  { code: 'custom_configuration', label: 'Custom configuration', note: 'Roles, workflows and rules built to fit.' },
  { code: 'custom_reports', label: 'Custom reports', note: 'Reports in the shape your board expects.' },
  { code: 'integrations', label: 'Integrations', note: 'Connect NyumbaSuite to what you already run.' },
  { code: 'managed_service', label: 'Managed service', note: 'We run the platform on your behalf.' },
  { code: 'flexible_terms', label: 'Flexible subscription terms', note: 'Billing arranged around your cycle.' },
];

function Section({ title, note }: { title: string; note?: string }) {
  return (
    <div className="q-section">
      <h2>{title}</h2>
      {note && <p>{note}</p>}
    </div>
  );
}

function Chips({ options, chosen, onToggle }: {
  options: [string, string][]; chosen: string[]; onToggle: (code: string) => void;
}) {
  return (
    <div className="q-chips">
      {options.map(([code, label]) => (
        <label key={code} className={`q-chip${chosen.includes(code) ? ' on' : ''}`}>
          <input type="checkbox" checked={chosen.includes(code)} onChange={() => onToggle(code)} />
          <span>{label}</span>
        </label>
      ))}
    </div>
  );
}

export function RequestQuote() {
  const { run, busy, error } = useMutation();
  const [done, setDone] = useState(false);
  const [errors, setErrors] = useState<Errors>({});
  const [services, setServices] = useState<string[]>([]);
  const [propertyTypes, setPropertyTypes] = useState<string[]>([]);
  const [collectionMethods, setCollectionMethods] = useState<string[]>([]);
  const [modules, setModules] = useState<string[]>([]);
  const [integrations, setIntegrations] = useState<string[]>([]);
  const [form, setForm] = useState({
    fullName: '', company: '', email: '', phone: '',
    clientType: '', propertyCount: '', unitCount: '', userCount: '',
    currentSoftware: '', softwareName: '', monthlyCollection: '',
    timeline: '', billingCycle: '', message: '',
  });

  const pricing = useApi<PricingResponse>('/api/v1/pricing');
  const model = pricing.data?.model.tiers.length ? pricing.data.model : FALLBACK_MODEL;
  const units = Number(form.unitCount) || 0;
  const baseline = units > 0 ? quoteUnits(model, units) : null;

  const set = (k: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const toggler = (set: (fn: (s: string[]) => string[]) => void) => (code: string) =>
    set((s) => (s.includes(code) ? s.filter((x) => x !== code) : [...s, code]));

  function validate(): Errors {
    const err: Errors = {};
    if (!form.fullName.trim()) err.fullName = 'Please tell us your name.';
    if (!form.email.trim()) err.email = 'An email is needed so we can send the quotation.';
    else if (!EMAIL_RE.test(form.email.trim())) err.email = 'That email does not look right.';
    if (!form.phone.trim()) err.phone = 'A phone number helps us reach you.';
    return err;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const err = validate();
    setErrors(err);
    if (Object.keys(err).length) return;

    const body = compact({
      fullName: form.fullName.trim(),
      company: form.company.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      clientType: form.clientType || undefined,
      propertyCount: numOrUndef(form.propertyCount),
      unitCount: numOrUndef(form.unitCount),
      userCount: form.userCount || undefined,
      currentSoftware: form.currentSoftware || undefined,
      // Only meaningful when they said they run something; sending it otherwise
      // would store the name of software they just told us they do not use.
      softwareName: form.currentSoftware === 'other_software' ? form.softwareName.trim() : undefined,
      monthlyCollection: form.monthlyCollection || undefined,
      timeline: form.timeline || undefined,
      billingCycle: form.billingCycle || undefined,
      message: form.message.trim(),
    });

    try {
      // compact() drops empties; an empty selection is meaningful here (nothing
      // wanted) but an empty array would be stripped, so the lists are attached
      // after rather than through it.
      await run('/api/v1/quote-requests', {
        body: { ...body, services, propertyTypes, collectionMethods, modules, integrations },
      });
      setDone(true);
      window.scrollTo(0, 0);
    } catch { /* useMutation surfaces the error below */ }
  }

  return (
    <div className="lp mk">
      <PublicHeader />

      {done ? (
        <div className="mk-success">
          <div className="mk-success-mark" aria-hidden>✓</div>
          <h1 className="mk-h2">Thank you, we have your request</h1>
          <p className="mk-p">
            We have sent a confirmation to <strong>{form.email.trim()}</strong>. Our team will come
            back to you with a quotation built around what you told us, rather than a standard
            package.
          </p>
          <div className="mk-cta-row" style={{ marginTop: 'var(--space-6)' }}>
            <Link className="btn btn-primary" to="/">Back to home</Link>
            <Link className="btn btn-secondary" to="/register">Get started in the meantime</Link>
          </div>
        </div>
      ) : (
        <>
          <section className="mk-hero">
            <span className="mk-eyebrow">Tailored quote</span>
            <h1 className="mk-title">Get a tailored NyumbaSuite quote</h1>
            <p className="mk-lede">
              Tell us a little about your portfolio and how you manage it today. We use it to
              recommend the right package and price — nothing here commits you to anything.
            </p>
          </section>

          <div className="mk-form-wrap">
            <form className="card" onSubmit={onSubmit} noValidate>
              {error && (
                <div style={{ marginBottom: 'var(--space-4)' }}>
                  <Alert tone="danger">{error.message}</Alert>
                </div>
              )}
              <div className="mk-form-grid">

                <Section title="About you" />
                <Field label="Full name" htmlFor="fullName" error={errors.fullName}>
                  <TextInput id="fullName" value={form.fullName} onChange={set('fullName')}
                    invalid={!!errors.fullName} autoComplete="name" />
                </Field>
                <Field label="Company (optional)" htmlFor="company">
                  <TextInput id="company" value={form.company} onChange={set('company')}
                    autoComplete="organization" />
                </Field>
                <Field label="Email" htmlFor="email" error={errors.email}>
                  <TextInput id="email" type="email" value={form.email} onChange={set('email')}
                    invalid={!!errors.email} autoComplete="email" />
                </Field>
                <Field label="Phone / WhatsApp" htmlFor="phone" error={errors.phone}>
                  <TextInput id="phone" type="tel" value={form.phone} onChange={set('phone')}
                    invalid={!!errors.phone} autoComplete="tel" placeholder="+254 7XX XXX XXX" />
                </Field>
                <div className="full">
                  <Field label="Which describes you best?" htmlFor="clientType">
                    <Select id="clientType" value={form.clientType} onChange={set('clientType')}>
                      <option value="">Select one</option>
                      {CLIENT_TYPES.map(([code, label]) => (
                        <option key={code} value={code}>{label}</option>
                      ))}
                    </Select>
                  </Field>
                </div>

                <Section title="Your portfolio"
                  note="Approximate is fine — we only need the shape of it." />
                <Field label="Number of properties" htmlFor="propertyCount">
                  <TextInput id="propertyCount" type="number" min={0} value={form.propertyCount}
                    onChange={set('propertyCount')} />
                </Field>
                <Field label="Number of units" htmlFor="unitCount"
                  hint={baseline && !baseline.free
                    ? `Standard progressive pricing for ${units} units is ${bandSum(baseline)} = ${formatKES(baseline.monthly)}/month. We will quote against that.`
                    : 'Any size. A tailored quote is not limited to large portfolios.'}>
                  <TextInput id="unitCount" type="number" min={0} value={form.unitCount}
                    onChange={set('unitCount')} />
                </Field>
                <div className="full">
                  <span className="field-label">What kind of property?</span>
                  <Chips options={PROPERTY_TYPES} chosen={propertyTypes}
                    onToggle={toggler(setPropertyTypes)} />
                </div>
                <Field label="How many people will use it?" htmlFor="userCount">
                  <Select id="userCount" value={form.userCount} onChange={set('userCount')}>
                    <option value="">Not sure yet</option>
                    {USER_COUNTS.map(([code, label]) => (
                      <option key={code} value={code}>{label}</option>
                    ))}
                  </Select>
                </Field>
                <Field label="Preferred billing" htmlFor="billingCycle">
                  <Select id="billingCycle" value={form.billingCycle} onChange={set('billingCycle')}>
                    <option value="">No preference</option>
                    <option value="monthly">Monthly</option>
                    <option value="annual">Annual</option>
                  </Select>
                </Field>

                <Section title="How you manage it today"
                  note="This tells us how much work moving across would be." />
                <Field label="What are you using now?" htmlFor="currentSoftware">
                  <Select id="currentSoftware" value={form.currentSoftware} onChange={set('currentSoftware')}>
                    <option value="">Select one</option>
                    {CURRENT_SOFTWARE.map(([code, label]) => (
                      <option key={code} value={code}>{label}</option>
                    ))}
                  </Select>
                </Field>
                {form.currentSoftware === 'other_software' && (
                  <Field label="Which software? (optional)" htmlFor="softwareName">
                    <TextInput id="softwareName" value={form.softwareName}
                      onChange={set('softwareName')} />
                  </Field>
                )}
                <div className="full">
                  <span className="field-label">How does rent reach you?</span>
                  <Chips options={COLLECTION_METHODS} chosen={collectionMethods}
                    onToggle={toggler(setCollectionMethods)} />
                </div>
                <div className="full">
                  <Field label="Roughly how much rent do you collect a month? (optional)"
                    htmlFor="monthlyCollection">
                    <Select id="monthlyCollection" value={form.monthlyCollection}
                      onChange={set('monthlyCollection')}>
                      <option value="">Prefer not to say</option>
                      {MONTHLY_COLLECTION.filter(([c]) => c !== 'undisclosed').map(([code, label]) => (
                        <option key={code} value={code}>{label}</option>
                      ))}
                    </Select>
                  </Field>
                </div>

                <Section title="What you need"
                  note="Pick anything that applies. It shapes the quote, not the price alone." />
                <div className="full">
                  <span className="field-label">What do you need help with?</span>
                  <Chips options={MODULES} chosen={modules} onToggle={toggler(setModules)} />
                </div>
                <div className="full">
                  <span className="field-label">Anything it needs to connect to?</span>
                  <Chips options={INTEGRATIONS} chosen={integrations}
                    onToggle={toggler(setIntegrations)} />
                </div>
                <Field label="When would you like to be running?" htmlFor="timeline">
                  <Select id="timeline" value={form.timeline} onChange={set('timeline')}>
                    <option value="">Not decided</option>
                    {TIMELINES.map(([code, label]) => (
                      <option key={code} value={code}>{label}</option>
                    ))}
                  </Select>
                </Field>

                <div className="full">
                  <span className="field-label">Would you like us to handle any of this for you?</span>
                  <div className="q-services">
                    {SERVICES.map((s) => (
                      <label key={s.code} className={`q-service${services.includes(s.code) ? ' on' : ''}`}>
                        <input type="checkbox" checked={services.includes(s.code)}
                          onChange={() => toggler(setServices)(s.code)} />
                        <span>
                          <strong>{s.label}</strong>
                          <small>{s.note}</small>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="full">
                  <Field label="Anything else we should know? (optional)" htmlFor="message">
                    <textarea id="message" className="input" rows={4} value={form.message}
                      onChange={set('message')} style={{ minHeight: 96, padding: 'var(--space-3)', resize: 'vertical' }} />
                  </Field>
                </div>
              </div>

              <Button type="submit" block loading={busy}>Request my quote</Button>
              <p className="field-hint" style={{ marginTop: 'var(--space-3)', textAlign: 'center' }}>
                We use this to understand your requirements and prepare a suitable proposal.
              </p>
              <p className="field-hint" style={{ marginTop: 'var(--space-2)', textAlign: 'center' }}>
                Would a standard plan do? <Link to="/#pricing">See progressive pricing</Link>,{' '}
                <Link to="/demo">book a demo</Link> or <Link to="/register">create your account</Link>.
              </p>
            </form>
          </div>
        </>
      )}

      <SiteFooter />
    </div>
  );
}
